import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, User, LogOut } from 'lucide-react';
import CartIcon from './CartIcon';
import AuthModal from './AuthModal';
import LanguageSelector from './LanguageSelector';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../translations/translations';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
  const [isMobileProductOpen, setIsMobileProductOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  
  // Ref for click outside detection
  const userDropdownRef = useRef(null);
  const productDropdownRef = useRef(null);

  const isActive = (href) => location.pathname === href;
  const isProductActive = () => ['/edible-cups', '/cutlery-tableware', '/edible-cup'].some(path => location.pathname.startsWith(path));

  // Close user dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setIsUserDropdownOpen(false);
      }
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target)) {
        setIsProductDropdownOpen(false);
      }
    };

    if (isUserDropdownOpen || isProductDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserDropdownOpen, isProductDropdownOpen]);

  const handleLogout = () => {
    logout();
    setIsUserDropdownOpen(false);
    navigate('/');
  };

  const toggleUserDropdown = () => {
    setIsUserDropdownOpen(!isUserDropdownOpen);
  };

  return (
    <header className="network-header">
      {/* Header Icons (Language + Login + Cart) */}
      <div className="header-icons">
        <LanguageSelector />
        {isAuthenticated ? (
          <div 
            className="user-dropdown-container"
            ref={userDropdownRef}
          >
            <button 
              className="header-user-btn logged-in"
              onClick={toggleUserDropdown}
            >
              <div className="user-avatar-small">
                {user?.firstName?.charAt(0) || 'U'}
              </div>
            </button>
            
            {isUserDropdownOpen && (
              <div className="user-dropdown-menu">
                <div className="user-dropdown-header">
                  <span className="user-name">{user?.firstName} {user?.lastName}</span>
                  <span className="user-email">{user?.email}</span>
                </div>
                <Link 
                  to="/account" 
                  className="user-dropdown-item" 
                  onClick={() => setIsUserDropdownOpen(false)}
                >
                  <User size={16} />
                  {t('nav.myAccount')}
                </Link>
                <button className="user-dropdown-item logout" onClick={handleLogout}>
                  <LogOut size={16} />
                  {t('nav.signOut')}
                </button>
              </div>
            )}
          </div>
        ) : (
          <button 
            className="header-user-btn"
            onClick={() => setIsAuthModalOpen(true)}
            title={t('nav.signIn')}
          >
            <User size={22} />
          </button>
        )}
        <CartIcon />
      </div>
      
      {/* Navigation Section */}
      <div className="nav-wrapper">
        {/* Desktop Navigation */}
        <nav className="network-nav desktop-nav">
          <Link
            to="/"
            className={`network-nav-link ${isActive('/') ? 'active' : ''}`}
          >
            {t('nav.home')}
          </Link>
          <Link
            to="/about"
            className={`network-nav-link ${isActive('/about') ? 'active' : ''}`}
          >
            {t('nav.about')}
          </Link>
          
          {/* Product Range Dropdown */}
          <div 
            className="product-dropdown-container"
            ref={productDropdownRef}
            onMouseEnter={() => setIsProductDropdownOpen(true)}
          >
            <span 
              className={`network-nav-link dropdown-trigger ${isProductActive() ? 'active' : ''}`}
              onClick={() => setIsProductDropdownOpen(!isProductDropdownOpen)}
            >
              {t('nav.productRange')}
              <ChevronDown size={16} className={`dropdown-arrow ${isProductDropdownOpen ? 'rotate' : ''}`} />
            </span>
            
            {isProductDropdownOpen && (
              <div className="product-dropdown-menu">
                <Link
                  to="/edible-cups"
                  className={`dropdown-item ${isActive('/edible-cups') ? 'active' : ''}`}
                  onClick={() => setIsProductDropdownOpen(false)}
                >
                  {t('nav.edibleCup')}
                </Link>
                <Link
                  to="/cutlery-tableware"
                  className={`dropdown-item ${isActive('/cutlery-tableware') ? 'active' : ''}`}
                  onClick={() => setIsProductDropdownOpen(false)}
                >
                  {t('nav.cutleryTableware')}
                </Link>
              </div>
            )}
          </div>

          <Link
            to="/certifications"
            className={`network-nav-link ${isActive('/certifications') ? 'active' : ''}`}
          >
            {t('nav.certifications')}
          </Link>
          <Link
            to="/blog"
            className={`network-nav-link ${isActive('/blog') ? 'active' : ''}`}
          >
            {t('nav.blog')}
          </Link>
          <Link
            to="/contact"
            className={`network-nav-link ${isActive('/contact') ? 'active' : ''}`}
          >
            {t('nav.contact')}
          </Link>
          <Link
            to="/shop"
            className={`network-nav-link ${isActive('/shop') ? 'active' : ''}`}
          >
            {t('nav.shop')}
          </Link>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="mobile-menu-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="mobile-nav">
          <Link
            to="/"
            className={`mobile-nav-link ${isActive('/') ? 'active' : ''}`}
            onClick={() => setIsMenuOpen(false)}
          >
            {t('nav.home')}
          </Link>
          <Link
            to="/about"
            className={`mobile-nav-link ${isActive('/about') ? 'active' : ''}`}
            onClick={() => setIsMenuOpen(false)}
          >
            {t('nav.about')}
          </Link>
          
          {/* Mobile Product Range Dropdown */}
          <div className="mobile-dropdown">
            <button 
              className={`mobile-nav-link mobile-dropdown-trigger ${isProductActive() ? 'active' : ''}`}
              onClick={() => setIsMobileProductOpen(!isMobileProductOpen)}
            >
              {t('nav.productRange')}
              <ChevronDown size={16} className={`dropdown-arrow ${isMobileProductOpen ? 'rotate' : ''}`} />
            </button>
            {isMobileProductOpen && (
              <div className="mobile-dropdown-menu">
                <Link
                  to="/edible-cups"
                  className={`mobile-dropdown-item ${isActive('/edible-cups') ? 'active' : ''}`}
                  onClick={() => {
                    setIsMenuOpen(false);
                    setIsMobileProductOpen(false);
                  }}
                >
                  {t('nav.edibleCup')}
                </Link>
                <Link
                  to="/cutlery-tableware"
                  className={`mobile-dropdown-item ${isActive('/cutlery-tableware') ? 'active' : ''}`}
                  onClick={() => {
                    setIsMenuOpen(false);
                    setIsMobileProductOpen(false);
                  }}
                >
                  {t('nav.cutleryTableware')}
                </Link>
              </div>
            )}
          </div>

          <Link
            to="/certifications"
            className={`mobile-nav-link ${isActive('/certifications') ? 'active' : ''}`}
            onClick={() => setIsMenuOpen(false)}
          >
            {t('nav.certifications')}
          </Link>
          <Link
            to="/blog"
            className={`mobile-nav-link ${isActive('/blog') ? 'active' : ''}`}
            onClick={() => setIsMenuOpen(false)}
          >
            {t('nav.blog')}
          </Link>
          <Link
            to="/contact"
            className={`mobile-nav-link ${isActive('/contact') ? 'active' : ''}`}
            onClick={() => setIsMenuOpen(false)}
          >
            {t('nav.contact')}
          </Link>
          <Link
            to="/shop"
            className={`mobile-nav-link ${isActive('/shop') ? 'active' : ''}`}
            onClick={() => setIsMenuOpen(false)}
          >
            {t('nav.shop')}
          </Link>
          
          {/* Mobile User Section */}
          <div className="mobile-user-section">
            {isAuthenticated ? (
              <>
                <Link
                  to="/account"
                  className="mobile-nav-link"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <User size={18} />
                  {t('nav.myAccount')}
                </Link>
                <button
                  className="mobile-nav-link logout-btn"
                  onClick={() => {
                    handleLogout();
                    setIsMenuOpen(false);
                  }}
                >
                  <LogOut size={18} />
                  {t('nav.signOut')}
                </button>
              </>
            ) : (
              <button
                className="mobile-nav-link login-btn"
                onClick={() => {
                  setIsMenuOpen(false);
                  setIsAuthModalOpen(true);
                }}
              >
                <User size={18} />
                {t('nav.signInRegister')}
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={isAuthModalOpen} 
        onClose={() => setIsAuthModalOpen(false)} 
      />
    </header>
  );
};

export default Header;